import React, { useState, useEffect } from 'react';
import "./Home.scss";
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getTeachersApi } from '../../api/teachers';
import dummyAvatar from '../../assets/images/dummy-user.jpeg';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const DEFAULT_TESTIMONIALS = [];

export const Home = () => {
  const [topTeachers, setTopTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);

  useEffect(() => {
    getTeachersApi()
      .then((res) => {
        if (res.data && Array.isArray(res.data) && res.data.length > 0) {
          // Sort by follower_count descending to show teachers with more followers
          const sorted = [...res.data].sort((a, b) => {
            const aFollowers = a.follower_count || a.followersCount || a.followers || 0;
            const bFollowers = b.follower_count || b.followersCount || b.followers || 0;
            if (bFollowers !== aFollowers) {
              return bFollowers - aFollowers;
            }
            return (b.review_count || 0) - (a.review_count || 0);
          });
          let list = sorted.slice(0, 12);
          if (list.length < 6) {
            const existingIds = new Set(list.map(t => t._id));
            const extra = DEFAULT_TESTIMONIALS.filter(t => !existingIds.has(t._id));
            list = [...list, ...extra];
          }
          setTopTeachers(list);
        } else {
          setTopTeachers(DEFAULT_TESTIMONIALS);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch top teachers:", err);
        setTopTeachers(DEFAULT_TESTIMONIALS);
      })
      .finally(() => setLoadingTeachers(false));
  }, []);

  return (
    <div className="home-page-new">
      {/* Hero Section */}
      <section className="hero-section position-relative overflow-hidden pt-5 pb-4">
        {/* Wavy Background Elements */}
        <div className="hero-bg-glow glow-1"></div>
        <div className="hero-bg-glow glow-2"></div>

        <Container fluid="xl">
          <div className="position-relative z-2">
            <Row className="align-items-center g-4">
              <Col lg={6}>
                <div className="hero-left-col position-relative">
                  {/* Welcome Badge */}
                  <div className="welcome-tag d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill bg-white shadow-sm mb-4">
                    <div className="heart-icon bg-purple text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '20px', height: '20px' }}>
                      <i className="bi bi-heart-fill" style={{ fontSize: '10px' }}></i>
                    </div>
                    <span className="fw-bold text-purple" style={{ letterSpacing: '1px', fontSize: '12px' }}>WELCOME TO YOOSPEAK</span>
                  </div>

                  <h1 className="hero-title fw-extrabold mb-3">
                    <span className="text-blue">Learn.</span> <span className="text-theme1">Teach.</span><br />
                    Connect. <span className="text-orange">Earn.</span>
                  </h1>

                  <div className="hero-subtitle mb-3 d-flex align-items-center">
                    <span className="line bg-gray-300 me-3" style={{ height: '2px', width: '50px' }}></span>
                    <span className="fw-semibold text-gray-500 tracking-wide text-uppercase text-sm">ALL IN ONE PLACE.</span>
                  </div>

                  <div className="hero-desc text-base text-gray-600 mb-4">
                    Yoospeak is the global app that helps you find teachers and find students instantly. Message, join classes, and practice anytime — all in one powerful platform. Live classes, communities, or teach and grow. Watch videos in our video feeds.
                  </div>

                  <div className="hero-buttons d-flex flex-wrap align-items-center gap-3">
                    <Link to="/signup" className="text-decoration-none">
                      <Button className="btn-join btn-theme1 btn-custom rounded-pill text-white d-flex align-items-center gap-2">
                        <i className="bi bi-rocket-takeoff-fill"></i> Join Yoospeak Today
                      </Button>
                    </Link>
                    <Link to="/about" className="text-decoration-none">
                      <Button variant="light" className="btn-how-it-works btn-custom rounded-pill bg-white d-flex align-items-center gap-2">
                        <div className="play-icon bg-purple text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px' }}>
                          <i className="bi bi-play-fill fs-6" style={{ marginLeft: '1px' }}></i>
                        </div>
                        See How It Works
                      </Button>
                    </Link>
                  </div>

                  <div className="trusted-users d-flex align-items-center gap-3 mt-3">
                    <div className="avatar-group d-flex">
                      <img src={require('../../assets/images/team1.jpeg')} alt="User" className="rounded-circle border border-2 border-white object-fit-cover shadow-sm" style={{ width: '40px', height: '40px' }} />
                      <img src={require('../../assets/images/team2.jpeg')} alt="User" className="rounded-circle border border-2 border-white object-fit-cover shadow-sm ms-n2" style={{ width: '40px', height: '40px' }} />
                      <img src={require('../../assets/images/team3.jpeg')} alt="User" className="rounded-circle border border-2 border-white object-fit-cover shadow-sm ms-n2" style={{ width: '40px', height: '40px' }} />
                      <img src={require('../../assets/images/team4.jpeg')} alt="User" className="rounded-circle border border-2 border-white object-fit-cover shadow-sm ms-n2" style={{ width: '40px', height: '40px' }} />
                    </div>
                    <div className="trusted-text">
                      <div className="stars text-warning fs-6 mb-1">
                        <i className="bi bi-star-fill me-1"></i><i className="bi bi-star-fill me-1"></i><i className="bi bi-star-fill me-1"></i><i className="bi bi-star-fill me-1"></i><i className="bi bi-star-fill"></i>
                      </div>
                      <div className="text-gray-500 text-xs fw-medium lh-sm text-start">
                        Loved by thousands of learners<br />and teachers worldwide
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              <Col lg={6}>
                <div className="hero-right-col position-relative text-center">
                  <div className="hero-image-wrapper position-relative text-center text-lg-end mt-5 mt-lg-0">
                    {/* Dot Grid Background Elements */}
                    <div className="dot-grid dot-grid-top"></div>
                    <div className="dot-grid dot-grid-bottom"></div>

                    {/* Hand-drawn Curly Arrow SVG */}
                    <div className="hand-drawn-arrow">
                      <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 80 C 30 50, 40 20, 80 40" stroke="#8b5cf6" strokeWidth="3" strokeLinecap="round" fill="none" />
                        <path d="M68 44 L 80 40 L 76 28" stroke="#8b5cf6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      </svg>
                    </div>

                    {/* Floating Badges */}
                    <div className="badge-float badge-users position-absolute bg-white shadow rounded-4 p-3 d-flex align-items-center gap-3 z-3">
                      <div className="icon-box bg-purple-light text-purple rounded-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                        <i className="bi bi-people-fill fs-5"></i>
                      </div>
                      <div className="text-start">
                        <div className="fw-bold text-dark fs-5 lh-1 mb-1">50K+</div>
                        <div className="text-gray-500 text-xs fw-semibold text-uppercase">Active Users</div>
                      </div>
                    </div>

                    <div className="badge-float badge-rating position-absolute bg-white shadow rounded-4 p-3 d-flex align-items-center gap-3 z-3">
                      <div className="icon-box bg-success-light text-success rounded-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                        <i className="bi bi-star-fill fs-5"></i>
                      </div>
                      <div className="text-start">
                        <div className="fw-bold text-dark fs-5 lh-1 mb-1">4.9</div>
                        <div className="text-gray-500 text-xs fw-semibold text-uppercase">App Rating</div>
                      </div>
                    </div>

                    <div className="badge-float badge-classes position-absolute bg-white shadow rounded-4 p-3 d-flex align-items-center gap-3 z-3">
                      <div className="icon-box bg-orange-light text-orange rounded-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                        <i className="bi bi-mortarboard-fill fs-5"></i>
                      </div>
                      <div className="text-start">
                        <div className="fw-bold text-dark fs-5 lh-1 mb-1">2K+</div>
                        <div className="text-gray-500 text-xs fw-semibold text-uppercase">Live Classes</div>
                      </div>
                    </div>

                    <div className="hero-image-bg d-inline-block position-relative shadow z-2">
                      <img src={require('../../assets/images/hero_woman_laptop.png')} alt="Yoospeak App User" className="img-fluid object-fit-cover w-100 h-100" />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Feature Cards Section */}
      <section className="features-section py-5">
        <Container fluid="xl">
          <Row className="g-4">
            {/* Card 1: Blue */}
            <Col md={6} lg={3}>
              <Link to="/teachers" className="text-decoration-none">
                <Card className="feature-card card-blue border-0 h-100 text-white d-flex flex-column justify-content-between gap-3 rounded-5 p-4 position-relative overflow-hidden transition-all">
                  <div className="icon-wrapper rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <i className="bi bi-patch-check-fill fs-3 text-white"></i>
                  </div>
                  <div className="wrapper">
                    <h3 className="fw-bold fs-4 mb-2">Find Certified<br />Teachers</h3>
                    <p className="text-white opacity-75 mb-0">Connect with verified teachers in 1-on-1 or group classes.</p>
                  </div>
                  <div className="arrow-btn rounded-circle d-flex align-items-center justify-content-center ms-auto transition-all" style={{ width: '40px', height: '40px', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <i className="bi bi-arrow-right text-white"></i>
                  </div>
                </Card>
              </Link>
            </Col>

            {/* Card 2: Green */}
            <Col md={6} lg={3}>
              <Link to="/communities" className="text-decoration-none">
                <Card className="feature-card card-green border-0 h-100 text-white d-flex flex-column justify-content-between gap-3 rounded-5 p-4 position-relative overflow-hidden transition-all">
                  <div className="icon-wrapper rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <i className="bi bi-people-fill fs-3 text-white"></i>
                  </div>
                  <div className="wrapper">
                    <h3 className="fw-bold fs-4 mb-2">Find New<br />Students</h3>
                    <p className="text-white opacity-75 mb-0">Reach learners and grow your teaching community.</p>
                  </div>
                  <div className="arrow-btn rounded-circle d-flex align-items-center justify-content-center ms-auto transition-all" style={{ width: '40px', height: '40px', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <i className="bi bi-arrow-right text-white"></i>
                  </div>
                </Card>
              </Link>
            </Col>

            {/* Card 3: Purple */}
            <Col md={6} lg={3}>
              <Link to="/messages" className="text-decoration-none">
                <Card className="feature-card card-purple border-0 h-100 text-white d-flex flex-column justify-content-between gap-3 rounded-5 p-4 position-relative overflow-hidden transition-all">
                  <div className="icon-wrapper rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <i className="bi bi-chat-dots-fill fs-3 text-white"></i>
                  </div>
                  <div className="wrapper">
                    <h3 className="fw-bold fs-4 mb-2">Easy<br />Communication</h3>
                    <p className="text-white opacity-75 mb-0">Chat, schedule, and share messages in real time.</p>
                  </div>
                  <div className="arrow-btn rounded-circle d-flex align-items-center justify-content-center ms-auto transition-all" style={{ width: '40px', height: '40px', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <i className="bi bi-arrow-right text-white"></i>
                  </div>
                </Card>
              </Link>
            </Col>

            {/* Card 4: Orange */}
            <Col md={6} lg={3}>
              <Link to="/pricing" className="text-decoration-none">
                <Card className="feature-card card-orange border-0 h-100 text-white d-flex flex-column justify-content-between gap-3 rounded-5 p-4 position-relative overflow-hidden transition-all">
                  <div className="icon-wrapper rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <i className="bi bi-wallet-fill fs-3 text-white"></i>
                  </div>
                  <div className="wrapper">
                    <h3 className="fw-bold fs-4 mb-2">Earn and<br />Grow</h3>
                    <p className="text-white opacity-75 mb-0">Get paid for private lessons, courses, and partnerships, or be a learner.</p>
                  </div>
                  <div className="arrow-btn rounded-circle d-flex align-items-center justify-content-center ms-auto transition-all" style={{ width: '40px', height: '40px', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <i className="bi bi-arrow-right text-white"></i>
                  </div>
                </Card>
              </Link>
            </Col>
          </Row>
        </Container>
      </section>

      {/* App Download Banner Section */}
      <section className="app-download-section">
        <Container fluid="xl">
          <div className="download-banner rounded-5 position-relative px-4 px-lg-5 py-5 py-lg-1 text-white shadow-lg overflow-hiddenn">
            {/* Background elements */}
            <div className="bg-glow position-absolute rounded-circle z-0" style={{ width: '400px', height: '400px', backgroundColor: 'rgba(103, 58, 183, 0.4)', filter: 'blur(80px)', top: '-10%', left: '10%' }}></div>
            <div className="bg-glow position-absolute rounded-circle z-0" style={{ width: '300px', height: '300px', backgroundColor: 'rgba(58, 183, 149, 0.2)', filter: 'blur(80px)', bottom: '-10%', right: '10%' }}></div>

            <Row className="align-items-center gx-5 position-relative z-2">
              <Col lg={6} className="text-center text-lg-start mb-5 mb-lg-0">
                <h6 className="text-uppercase text-purple-light fw-bold mb-3" style={{ letterSpacing: '2px', fontSize: '13px' }}>YOOSPEAK APP</h6>
                <div className="section-heading text-white mb-4">
                  One App.<br />
                  Unlimited <span className="text-purple-light">Possibilities.</span>
                </div>
                <div className="description text-white text-opacity-75">
                  Download Yoospeak today and carry the world's most powerful learning and teaching community in your pocket.
                </div>
                <div className="d-flex flex-wrap justify-content-center justify-content-lg-start gap-3 mt-4">
                  <Link to={'https://apps.apple.com/in/app/yoospeak/id6746839854'} target="_blank" className="text-decoration-none">
                    <Button variant="light" className="store-btn d-flex align-items-center gap-2">
                      <i className="bi bi-apple"></i> App Store
                    </Button>
                  </Link>
                  <Link to={'https://play.google.com/store/apps/details?id=com.yoospeak.learning'} target="_blank" className="text-decoration-none">
                    <Button variant="outline-light" className="store-btn d-flex align-items-center gap-2">
                      <i className="bi bi-google-play"></i> Google Play
                    </Button>
                  </Link>
                </div>
              </Col>

              {/* Vector Smartphone Mockup */}
              <Col lg={6} className="text-center d-flex justify-content-center align-items-center">
                <div className="phone-mockup-container position-relative">
                  <div className="phone-bezel">
                    <div className="phone-notch"></div>
                    <div className="phone-screen">

                      {/* App Header */}
                      <div className="phone-app-header d-flex justify-content-between align-items-center px-3 py-2 border-bottom border-dark border-opacity-20">
                        <span className="phone-logo fw-bold">Yoospeak</span>
                        <i className="bi bi-list fs-5 text-white"></i>
                      </div>

                      {/* App Main Content */}
                      <div className="phone-app-body px-3 py-3">

                        {/* Practice Banner */}
                        <div className="phone-banner mb-3 p-3 rounded-4 text-start position-relative overflow-hidden">
                          <div className="banner-tag text-uppercase fw-bold">PRACTICE ANYTIME</div>
                          <div className="banner-title fw-extrabold fs-5 mt-1">English Vocab</div>
                          <div className="glow-circle"></div>
                        </div>

                        {/* Action Cards (Chat & Live Class) */}
                        <div className="row g-2 mb-3">
                          <div className="col-6">
                            <div className="phone-action-card card-blue p-2 rounded-3 text-start d-flex flex-column gap-1 pointer">
                              <i className="bi bi-chat-dots-fill fs-5"></i>
                              <span className="card-label fw-bold">Chat</span>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="phone-action-card card-green p-2 rounded-3 text-start d-flex flex-column gap-1 pointer">
                              <i className="bi bi-camera-video-fill fs-5"></i>
                              <span className="card-label fw-bold">Live Class</span>
                            </div>
                          </div>
                        </div>

                        {/* Continue Learning List */}
                        <div className="phone-learning-section text-start">
                          <div className="section-title text-uppercase fw-bold mb-2">Continue Learning</div>

                          <div className="phone-learning-item d-flex align-items-center justify-content-between p-2 rounded-3 mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <div className="item-icon bg-purple-light text-purple rounded d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }}>
                                <i className="bi bi-mic-fill fs-6"></i>
                              </div>
                              <div>
                                <div className="item-name fw-bold">Daily Conversation</div>
                                <div className="item-progress bg-slate-700 rounded-pill mt-1" style={{ height: '4px', width: '80px', backgroundColor: '#334155' }}>
                                  <div className="progress-bar bg-success rounded-pill h-100" style={{ width: '75%' }}></div>
                                </div>
                              </div>
                            </div>
                            <span className="percent-badge text-success fw-bold">75%</span>
                          </div>

                          <div className="phone-learning-item d-flex align-items-center justify-content-between p-2 rounded-3">
                            <div className="d-flex align-items-center gap-2">
                              <div className="item-icon bg-orange-light text-orange rounded d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }}>
                                <i className="bi bi-briefcase-fill fs-6"></i>
                              </div>
                              <div>
                                <div className="item-name fw-bold">Business English</div>
                                <div className="item-desc text-gray-400">Professional communication</div>
                              </div>
                            </div>
                            <span className="badge bg-orange rounded-pill text-uppercase" style={{ fontSize: '8px', padding: '3px 6px' }}>NEW</span>
                          </div>
                        </div>
                      </div>

                      {/* Phone Bottom Nav Bar */}
                      <div className="phone-nav-bar d-flex justify-content-around align-items-center py-2 bg-dark border-top border-dark border-opacity-50">
                        <i className="bi bi-house-door-fill text-purple"></i>
                        <i className="bi bi-book text-gray-500"></i>
                        <i className="bi bi-people text-gray-500"></i>
                        <i className="bi bi-person text-gray-500"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <Container fluid="xl">
          <div className="stats-container bg-white shadow rounded-4 p-4">
            <Row className="g-4">
              <Col xs={6} lg={3} className="stat-item position-relative">
                <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-3">
                  <div className="stat-icon bg-purple-light text-purple rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '56px', height: '56px' }}>
                    <i className="bi bi-people-fill fs-4"></i>
                  </div>
                  <div className="text-center text-md-start">
                    <h3 className="fw-extrabold text-dark mb-0 fs-3 lh-1">50K+</h3>
                    <span className="text-gray-500 fw-medium small text-uppercase">Active Users</span>
                  </div>
                </div>
              </Col>
              <Col xs={6} lg={3} className="stat-item position-relative">
                <div className="divider d-none d-lg-block position-absolute top-50 start-0 translate-middle-y bg-gray-200" style={{ width: '2px', height: '50px' }}></div>
                <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-3">
                  <div className="stat-icon bg-orange-light text-orange rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '56px', height: '56px' }}>
                    <i className="bi bi-mortarboard-fill fs-4"></i>
                  </div>
                  <div className="text-center text-md-start">
                    <h3 className="fw-extrabold text-dark mb-0 fs-3 lh-1">2K+</h3>
                    <span className="text-gray-500 fw-medium small text-uppercase">Expert Teachers</span>
                  </div>
                </div>
              </Col>
              <Col xs={6} lg={3} className="stat-item position-relative">
                <div className="divider d-none d-lg-block position-absolute top-50 start-0 translate-middle-y bg-gray-200" style={{ width: '2px', height: '50px' }}></div>
                <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-3">
                  <div className="stat-icon bg-success-light text-success rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '56px', height: '56px' }}>
                    <i className="bi bi-play-btn-fill fs-4"></i>
                  </div>
                  <div className="text-center text-md-start">
                    <h3 className="fw-extrabold text-dark mb-0 fs-3 lh-1">10K+</h3>
                    <span className="text-gray-500 fw-medium small text-uppercase">Live Classes</span>
                  </div>
                </div>
              </Col>
              <Col xs={6} lg={3} className="stat-item position-relative">
                <div className="divider d-none d-lg-block position-absolute top-50 start-0 translate-middle-y bg-gray-200" style={{ width: '2px', height: '50px' }}></div>
                <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-3">
                  <div className="stat-icon bg-blue-light text-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '56px', height: '56px' }}>
                    <i className="bi bi-globe fs-4"></i>
                  </div>
                  <div className="text-center text-md-start">
                    <h3 className="fw-extrabold text-dark mb-0 fs-3 lh-1">120+</h3>
                    <span className="text-gray-500 fw-medium small text-uppercase">Countries</span>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section py-5">
        <Container fluid="xl">
          <div className="text-center mb-4">
            <h6 className="text-purple fw-bold text-uppercase tracking-widest mb-2" style={{ fontSize: '13px', letterSpacing: '1px' }}>WHAT OUR COMMUNITY SAYS</h6>
            <h2 className="fw-extrabold text-dark display-6 position-relative d-inline-block">
              Loved by Learners & Teachers
              <span className="position-absolute" style={{ right: '-35px', bottom: '15px' }}>
                <i className="bi bi-heart text-purple fs-4"></i>
              </span>
            </h2>
          </div>

          {topTeachers.length > 0 ? (
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={24}
              slidesPerView={1}
              breakpoints={{
                768: { slidesPerView: 2 },
                992: { slidesPerView: 3 }
              }}
              pagination={{ clickable: true }}
              autoplay={{ delay: 7000, disableOnInteraction: false }}
              loop={topTeachers.length > 3}
              className="testimonials-swiper pb-5"
            >
              {topTeachers.map((teacher, index) => {
                const colors = [
                  { bg: '#fdf4fc', iconClass: 'text-purple' },
                  { bg: '#eefdf4', iconClass: 'text-success' },
                  { bg: '#fff6ec', iconClass: 'text-orange' }
                ];
                const theme = colors[index % colors.length];
                const rating = teacher.average_rating || teacher.rating || 5;
                const followers = teacher.follower_count || teacher.followersCount || teacher.followers || 0;

                return (
                  <SwiperSlide key={teacher._id || index} className="h-auto">
                    <div className="testimonial-card card-hover rounded-4 p-4 h-100 position-relative d-flex flex-column" style={{ backgroundColor: theme.bg }}>
                      <div className="d-flex gap-2">
                        <div className="quotes-icon" style={{ width: '42px', minWidth: '42px', height: '42px' }}>
                          <i className={`bi bi-quote position-absolute ${theme.iconClass} opacity-25 lh-1`} style={{ fontSize: '2.5rem' }}></i>
                        </div>
                        <div className="wrapper mb-4">
                          <p className="text-black text-opacity-75 text-sm mb-2 position-relative z-1">
                            "{teacher.description && teacher.description.length > 10
                              ? (teacher.description.length > 120 ? teacher.description.substring(0, 120) + '...' : teacher.description)
                              : 'Yoospeak has helped me reach so many students and share my passion for teaching globally. The tools are fantastic!'}"
                          </p>

                          {/* Followers count label */}
                          <div className="followers-badge d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill bg-white text-xs fw-bold text-gray-700 border border-light-subtle shadow-sm" style={{ width: 'fit-content', alignSelf: 'flex-start' }}>
                            <i className="bi bi-people-fill text-purple"></i>
                            <span>{followers.toLocaleString()} followers</span>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-end justify-content-between mt-auto">
                        <div className="d-flex align-items-center gap-2">
                          <img src={teacher.profile_picture || dummyAvatar} alt={teacher.full_name} className="rounded-circle object-fit-cover" style={{ width: '42px', minWidth: '42px', height: '42px' }} />
                          <div>
                            <h6 className="fw-bold text-dark mb-0 fs-6 lh-1 d-flex align-items-center gap-1">
                              {teacher.full_name}
                              {teacher.is_blue_verified && <i className="bi bi-patch-check-fill text-primary text-xs" title="Verified Teacher" />}
                            </h6>
                            <span className="text-gray-500 small text-capitalize">{teacher.user_type || 'Teacher'}</span>
                          </div>
                        </div>
                        <div className="stars text-warning fs-6 d-flex align-items-center">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className={`bi bi-star-fill ${i < Math.round(rating) ? '' : 'opacity-25'} me-1`} style={{ fontSize: '13px' }}></i>
                          ))}
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          ) : (
            <div className="text-center text-muted py-5">No top teachers found.</div>
          )}
        </Container>
      </section>

    </div>
  );
}
